import { onBeforeUnmount, onMounted, watch } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const MAX_TIMER_DELAY = 2147483647;

export const createAgentBusinessHoursAccessCoordinator = ({
  store,
  router,
  getAccountId,
}) => {
  let started = false;
  let timer = null;
  let pendingRefresh = null;
  let pendingAccountId = null;
  let refresh;

  const clearEvaluationTimer = () => {
    if (timer) window.clearTimeout(timer);
    timer = null;
  };

  const redirectIfRestricted = access => {
    if (access?.mode !== 'restricted') return;
    if (router.currentRoute.value.name === 'inbox_view') return;

    router.replace({
      name: 'inbox_view',
      params: { accountId: access.account_id },
    });
  };

  function scheduleEvaluation(access) {
    clearEvaluationTimer();
    if (!access?.next_evaluation_at) return;

    const delay = Math.max(
      0,
      Math.min(
        Date.parse(access.next_evaluation_at) - Date.now(),
        MAX_TIMER_DELAY
      )
    );
    timer = window.setTimeout(() => refresh(), delay);
  }

  function reconcile(access) {
    redirectIfRestricted(access);
    scheduleEvaluation(access);
  }

  refresh = () => {
    const accountId = Number(getAccountId());
    if (!accountId) return Promise.resolve();
    if (pendingRefresh && pendingAccountId === accountId) return pendingRefresh;

    pendingAccountId = accountId;
    pendingRefresh = Promise.resolve(
      store.dispatch('agentBusinessHoursAccess/fetch', accountId)
    )
      .then(access => {
        if (Number(getAccountId()) === accountId) reconcile(access);
        return access;
      })
      .finally(() => {
        if (pendingAccountId === accountId) {
          pendingRefresh = null;
          pendingAccountId = null;
        }
      });
    return pendingRefresh;
  };

  const applyRestrictedEnvelope = access => {
    if (Number(access?.account_id) !== Number(getAccountId())) return;
    store.dispatch('agentBusinessHoursAccess/applyEnvelope', access);
    reconcile(access);
  };

  const refreshWhenVisible = () => {
    if (document.visibilityState === 'visible') refresh();
  };

  const start = () => {
    if (started) return pendingRefresh || Promise.resolve();
    started = true;
    emitter.on(
      BUS_EVENTS.AGENT_BUSINESS_HOURS_ACCESS_RESTRICTED,
      applyRestrictedEnvelope
    );
    emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, refresh);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return refresh();
  };

  const stop = () => {
    if (!started) return;
    started = false;
    clearEvaluationTimer();
    emitter.off(
      BUS_EVENTS.AGENT_BUSINESS_HOURS_ACCESS_RESTRICTED,
      applyRestrictedEnvelope
    );
    emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, refresh);
    window.removeEventListener('focus', refresh);
    document.removeEventListener('visibilitychange', refreshWhenVisible);
  };

  return { start, stop, refresh, reconcile };
};

export const useAgentBusinessHoursAccessCoordinator = ({
  store,
  router,
  accountId,
}) => {
  const coordinator = createAgentBusinessHoursAccessCoordinator({
    store,
    router,
    getAccountId: () => accountId.value,
  });

  watch(accountId, (current, previous) => {
    if (current && current !== previous) coordinator.refresh();
  });
  onMounted(coordinator.start);
  onBeforeUnmount(coordinator.stop);

  return coordinator;
};
